// MobileStatsPage — Vollbild-Statistik-Ebene: bündelt die Inhalte der Desktop-
// Kommandobrücke (Fortschritt, Mana-Kurve, Mana-Basis, Ziehchancen, Wantlist-
// Kurzfassung, Tokens, Kartenvorschläge) für Fenster, in denen keine seitliche
// Brücke Platz hat. Reine Anzeige: die Mana-Kurve bekommt keinen Klick-Filter
// (Desktop-only), alle Sektionen starten offen.
import React, { useCallback, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../../../../context/AuthContext';
import { useToast } from '../../../../ui/Toast';
import ProgressBar from '../../../../ui/ProgressBar';
import ManaCurve from '../../components/ManaCurve';
import HypergeometricStats from '../../components/HypergeometricStats';
import ManaBaseSection from '../../components/bridge/ManaBaseSection';
import WantlistRow from '../../components/bridge/WantlistRow';
import { TokenSection } from '../../components/TokenSection';
import SuggestionTile from '../../components/SuggestionTile';
import BridgeSection from '../../components/bridge/BridgeSection';
import { useCardSuggestions } from '../../hooks/useCardSuggestions';
import { useDeckStats } from '../../hooks/useDeckFiltering';
import { formatPrice } from '../../utils/formatPrice';

export default function MobileStatsPage({
  deck,
  statsCards,
  excludedCategoryNames,
  targetCount,
  deckTotals,
  canEditDeck,
  isOwnProfile,
  onDeckUpdate,
  onClose,
  onExportTokens,
  onOpenCard,
  cardActions,
  undo,
}) {
  const { isAdmin } = useAuth();
  const { push: pushToast } = useToast();
  const [curveOpen, setCurveOpen] = useState(true);
  const [manaBaseOpen, setManaBaseOpen] = useState(true);
  const [oddsOpen, setOddsOpen] = useState(true);

  const deckStats = useDeckStats(statsCards);

  const { cards: suggestedCards, dismissAll, dismissCard } = useCardSuggestions(deck?.id);

  // DELETE kann fehlschlagen (json() wirft bei jeder Nicht-2xx-Antwort) — ohne
  // Fang liefe das als unbehandelte Promise-Ablehnung durch.
  const handleDismissSuggestion = useCallback((scryfallId) => {
    dismissCard(scryfallId).catch(() => {
      pushToast({ tone: 'error', message: 'Vorschlag konnte nicht verworfen werden.' });
    });
  }, [dismissCard, pushToast]);

  const handleDismissAllSuggestions = useCallback(() => {
    dismissAll().catch(() => {
      pushToast({ tone: 'error', message: 'Vorschläge konnten nicht verworfen werden.' });
    });
  }, [dismissAll, pushToast]);

  // Übernehmen fügt still hinzu (kein Erfolgs-Toast) und löscht den Vorschlag
  // serverseitig, sonst holt der nächste Poll ihn zurück.
  const handleAddSuggestion = useCallback(async (scryfallId) => {
    const result = await cardActions.addCardByScryfallId(scryfallId);
    if (result?.card) {
      undo.registerAdd(result.card);
      handleDismissSuggestion(scryfallId);
    } else if (result?.duplicate) {
      const suggestionCard = suggestedCards.find((c) => c.id === scryfallId);
      pushToast({ tone: 'info', message: `${suggestionCard?.name ?? 'Karte'} ist bereits im Deck` });
    }
  }, [cardActions, undo, suggestedCards, pushToast, handleDismissSuggestion]);

  return (
    <div className="mobile-stats-page">
      <div className="mobile-stats-page__header">
        <button type="button" className="mobile-stats-page__back" onClick={onClose}>
          <ArrowLeft size={16} aria-hidden /> Zurück
        </button>
        <span className="mobile-stats-page__title">{deck?.name || 'Unbenanntes Deck'}</span>
      </div>

      <div className="mobile-stats-page__body">
        <BridgeSection.Static>
          <div className="bridge-progress">
            <span
              className={`bridge-progress__num${
                targetCount && deckTotals.count >= targetCount ? ' bridge-progress__num--done' : ''
              }`}
            >
              <strong>{deckTotals.count}</strong>{targetCount ? ` / ${targetCount}` : ' Karten'}
            </span>
            {targetCount && (
              <ProgressBar
                value={Math.min(deckTotals.count, targetCount)}
                max={targetCount}
                label={`${deckTotals.count} von ${targetCount} Karten`}
              />
            )}
          </div>
          <div className="mobile-stats-page__value">{formatPrice(deckTotals.value)}</div>
        </BridgeSection.Static>

        <BridgeSection
          label="Mana-Kurve"
          value={deckStats.avgCmc != null ? `Ø ${deckStats.avgCmc.toFixed(1).replace('.', ',')}` : null}
          open={curveOpen}
          onToggle={() => setCurveOpen((o) => !o)}
        >
          {/* highlightedCmc explizit null: ManaCurve prüft `highlightedCmc !== null` für
              das Dimmen der Nicht-Auswahl — undefined träfe das auch, ohne dass hier je ein
              Balken markiert wird (kein onBarClick, reine Anzeige). */}
          <ManaCurve cards={statsCards} deckStats={deckStats} highlightedCmc={null} />
        </BridgeSection>

        <BridgeSection
          label="Mana-Basis"
          value={`${deckStats.landCount ?? 0} Länder`}
          open={manaBaseOpen}
          onToggle={() => setManaBaseOpen((o) => !o)}
        >
          <ManaBaseSection deckStats={deckStats} canEdit={canEditDeck} deckId={deck?.id} onDeckUpdate={onDeckUpdate} />
        </BridgeSection>

        <BridgeSection
          label="Ziehchancen"
          open={oddsOpen}
          onToggle={() => setOddsOpen((o) => !o)}
        >
          <HypergeometricStats cards={statsCards} excludedCategoryNames={excludedCategoryNames} />
        </BridgeSection>

        <WantlistRow deck={deck} isOwnProfile={isOwnProfile} />

        <TokenSection tokens={deck?.tokens} onExport={onExportTokens} />

        {isAdmin && canEditDeck && (
          <SuggestionTile
            cards={suggestedCards}
            onAdd={handleAddSuggestion}
            onDismissCard={handleDismissSuggestion}
            onDismissAll={handleDismissAllSuggestions}
            onOpen={onOpenCard}
          />
        )}
      </div>
    </div>
  );
}
